/**
 * Zeichnungen zu Übungen aus dem Startbestand (25.10.2026).
 *
 * Quelle ist **Everkinetic** (CC BY-SA 4.0): je Übung zwei Strichzeichnungen,
 * Anfangs- und Endstellung — im Wechsel gezeigt ergibt das eine kleine
 * Animation, ohne GIF-Lizenz. Fotos aus offenen Datenbanken (wger) sind bewusst
 * nicht dabei: Herkunft und KI-Kennzeichnung ließen sich nicht prüfen.
 *
 * Zugeordnet wird **nur über `catalogKey`**, nie über den Namen und nie vom
 * Grundtyp geerbt: Eine selbst angelegte Übung oder Variante bekommt kein Bild
 * statt eines geratenen. Fehlt ein Bild, sagt die Oberfläche es ausdrücklich.
 *
 * `approx`: Die Zeichnung zeigt eine nahe Ausführung (etwa sitzend statt
 * stehend) — die Oberfläche schreibt das dazu.
 *
 * Die Dateien liegen in `public/exercises/` (nur die Linien, Fläche und
 * Hintergrund entfernt, damit sie als Maske die Textfarbe annehmen — hell wie
 * dunkel). Lizenz und Änderungen: `public/exercises/LIZENZ.md`.
 */

interface ArtEntry {
  /** Nummer bei Everkinetic — für die Herkunft in `LIZENZ.md`. */
  everkinetic: string;
  approx?: true;
}

const EXERCISE_ART: Record<string, ArtEntry> = {
  squat: { everkinetic: '0122' },
  'squat-front': { everkinetic: '0138' },
  legpress: { everkinetic: '0127' },
  lunge: { everkinetic: '0115' },
  rdl: { everkinetic: '0118' },
  legcurl: { everkinetic: '0117' },
  legextension: { everkinetic: '0142' },
  calfraise: { everkinetic: '0282' },
  bench: { everkinetic: '0042' },
  'bench-incline': { everkinetic: '0043' },
  'bench-db': { everkinetic: '0055' },
  'bench-incline-db': { everkinetic: '0080' },
  pushup: { everkinetic: '0077' },
  dips: { everkinetic: '0054' },
  // Sitzend statt stehend.
  ohp: { everkinetic: '0004', approx: true },
  // Einarmig statt beidarmig.
  'ohp-db': { everkinetic: '0038', approx: true },
  chestfly: { everkinetic: '0048' },
  'triceps-pushdown': { everkinetic: '0205' },
  pullup: { everkinetic: '0087' },
  // Enger Griff (V-Griff).
  latpulldown: { everkinetic: '0096', approx: true },
  // Untergriff.
  'row-barbell': { everkinetic: '0026', approx: true },
  'row-cable': { everkinetic: '0025' },
  curl: { everkinetic: '0211' },
  'curl-hammer': { everkinetic: '0213' },
  backextension: { everkinetic: '0103' },
  // Mit Langhantel statt Rad.
  abwheel: { everkinetic: '0286', approx: true },
};

export interface ExerciseArt {
  start: string;
  end: string;
  approx: boolean;
}

/** Bild zur Übung — oder `null`, dann zeigt die Oberfläche „kein Bild“. */
export function exerciseArt(catalogKey: string | null | undefined): ExerciseArt | null {
  if (!catalogKey) return null;
  const entry = EXERCISE_ART[catalogKey];
  if (!entry) return null;
  const base = `${import.meta.env.BASE_URL}exercises/${catalogKey}`;
  return { start: `${base}-a.svg`, end: `${base}-b.svg`, approx: entry.approx === true };
}

/** Alle Schlüssel mit Bild — für den Test, dass jede Datei da ist. */
export const ART_KEYS = Object.keys(EXERCISE_ART);
